import { useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import {
  getSummaryReport,
  createSummaryReportReview,
  updateSummaryReportReview,
} from 'apis/summaryReport.api';
import { toast } from 'layout';
import { getRoles, checkIsAllowedCRUDFromContract } from 'utils/func';
import { getGeneralInfoProjectForCouncil } from 'apis/projects.api';
import CommentModal from 'features/SubmitProjectProposal/components/CommentModal';
import ViewHistoryModal from 'features/SubmitProjectProposal/components/ViewHistoryModal';

import ReviewForm from 'features/SubmitProjectProposal/components/ReviewForm';
import { TOAST } from 'constant';
import { Can } from 'components/Can';
import { createSummaryReportReviewSchema } from 'features/SubmitProjectProposal/validation';

export default function LiquidationDocumentsChecker({
  projectId: passedProjectId,
  summaryReportId: passedSummaryReportId,
}) {
  const searchParams = useParams();
  const projectId = passedProjectId || searchParams?.projectId;
  const summaryReportId = passedSummaryReportId || searchParams?.summaryReportId;
  const { t } = useTranslation();
  const roles = getRoles();

  const [visible, setVisible] = useState(false);
  const [isViewHistoryVisible, setIsViewHistoryVisible] = useState(false);
  const [requestReviseData, setRequestReviseData] = useState({});

  const {
    handleSubmit,
    control,
    getValues,
    reset,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    shouldFocusError: true,
    resolver: yupResolver(createSummaryReportReviewSchema),
  });
  // // #region call api
  const { data: summaryReportData, refetch } = useQuery(['summaryReport', summaryReportId], () =>
    getSummaryReport(summaryReportId)
  );

  const summaryReport = useMemo(() => summaryReportData?.data, [summaryReportData]);

  const summaryReportRevises = useMemo(
    () => summaryReportData?.data?.summaryReport?.summaryReportRevises ?? [],
    [summaryReportData]
  );

  const isRequestRevise = useMemo(
    () => summaryReportRevises?.findIndex((item) => item?.isRevise) !== -1,
    [summaryReportRevises]
  );

  const { data: generalInfoProjectData } = useQuery(
    ['GeneralInfoProject', projectId],
    () => getGeneralInfoProjectForCouncil(projectId),
    {
      staleTime: 1000 * 60 * 5,
    }
  );
  const generalInfoProjectProposal = useMemo(
    () => generalInfoProjectData?.data?.projectProposal,
    [generalInfoProjectData]
  );

  const { mutate: mutateCreateSummaryReportReview } = useMutation(createSummaryReportReview);
  const { mutate: mutateUpdateSummaryReportReview } = useMutation(updateSummaryReportReview);

  const handleCloseCommentModal = () => {
    setVisible(false);
    setRequestReviseData({});
    reset();
  };

  const onSubmit = async () => {
    const body = {
      comment: getValues('comment'),
      deadline: getValues('deadline') || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
    };
    if (requestReviseData?.id) {
      mutateUpdateSummaryReportReview(
        { summaryReportId, reviewId: requestReviseData.id, body },
        {
          onSuccess: () => {
            toast(TOAST.SUCCESS, t('submitDocument.toast.updateSuccess'));
            setVisible(false);
            refetch();
          },
          onError: () => {
            toast(TOAST.ERROR, t('submitDocument.toast.error'));
          },
        }
      );
    } else {
      mutateCreateSummaryReportReview(
        { summaryReportId, body },
        {
          onSuccess: () => {
            toast(TOAST.SUCCESS, t('submitDocument.toast.success'));
            setVisible(false);
            refetch();
          },
          onError: () => {
            toast(TOAST.ERROR, t('submitDocument.toast.error'));
          },
        }
      );
    }
  };

  return (
    <>
      <ReviewForm summaryReport={summaryReport} setIsViewHistoryVisible={setIsViewHistoryVisible} />
      {checkIsAllowedCRUDFromContract(generalInfoProjectProposal?.projectType, roles) && (
        <Can do="update" on="summary-report">
          <div className="flex justify-content-end mt-2 mr-4 gap-2">
            {summaryReportRevises?.length > 0 && (
              <Button
                type="button"
                className="flex justify-content-center items-center"
                onClick={() => setIsViewHistoryVisible(true)}
                severity="info"
              >
                {t('submitDocument.viewHistory')}
              </Button>
            )}
            {isRequestRevise && (
              <Button
                type="button"
                className="flex justify-content-center items-center"
                onClick={() => {
                  setRequestReviseData(summaryReportRevises?.[summaryReportRevises.length - 1]);
                  setVisible(true);
                }}
                severity="warning"
              >
                {t('submitDocument.permissionUpdateComment')}
              </Button>
            )}

            {!isRequestRevise && summaryReport?.summaryReport?.submittedAt && (
              <Button
                type="button"
                className="flex justify-content-center items-center"
                onClick={() => setVisible(true)}
                tooltip={t('submitDocument.note')}
                tooltipOptions={{
                  position: 'left',
                }}
              >
                {t('submitDocument.permissionUpdateReport')}
              </Button>
            )}

            {!isRequestRevise && !summaryReport?.summaryReport?.submittedAt && (
              <Button
                className="flex justify-content-center items-center cursor-auto"
                severity="secondary"
                tooltip={t('submitDocument.note')}
                tooltipOptions={{ position: 'left' }}
              >
                {t('submitDocument.permissionUpdateReport')}
              </Button>
            )}
          </div>
        </Can>
      )}
      <CommentModal
        visible={visible}
        onClose={handleCloseCommentModal}
        onSubmit={onSubmit}
        handleSubmit={handleSubmit}
        control={control}
        errors={errors}
        data={requestReviseData}
      />
      <ViewHistoryModal
        visible={isViewHistoryVisible}
        setVisible={setIsViewHistoryVisible}
        history={summaryReportData?.data?.summaryReport?.summaryReportRevises}
      />
    </>
  );
}

LiquidationDocumentsChecker.propTypes = {
  projectId: PropTypes.string,
  summaryReportId: PropTypes.string,
};

LiquidationDocumentsChecker.defaultProps = {
  projectId: '',
  summaryReportId: '',
};
